import React, { useState } from 'react';
import { Place, EmailData } from '../../../types/results.types';
import BulkDMUnlockModal from '../BulkDMUnlockModal/BulkDMUnlockModal';
import EmailVerificationModal from '../EmailVerificationModal/EmailVerificationModal';
import { isVerifiable } from '../../../utils/emailVerifiable';
import { findablePeopleIds } from '../../../utils/rowActions';

interface FloatingSelectionBarProps {
  selectedIds: Set<number>;
  places: Place[];
  onExport: () => void;
  onClear: () => void;
  searchId?: number;
}

const FloatingSelectionBar: React.FC<FloatingSelectionBarProps> = ({
  selectedIds,
  places,
  onExport,
  onClear,
  searchId,
}) => {
  const [isDMModalOpen, setIsDMModalOpen] = useState(false);
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [modalLockedPersonIds, setModalLockedPersonIds] = useState<number[]>([]);
  const [modalPersonEmails, setModalPersonEmails] = useState<EmailData[]>([]);
  const [modalPlaceEmails, setModalPlaceEmails] = useState<EmailData[]>([]);

  if (selectedIds.size === 0) return null;

  const selected = places.filter((place) => selectedIds.has(place.id));

  const lockedPersonIds = selected.flatMap(findablePeopleIds);

  const collectVerifiableEmails = (_source: 'person' | 'place_data' | undefined, emails: EmailData[]) =>
    emails.filter(isVerifiable);

  const allPersonEmails: EmailData[] = selected.flatMap((place) =>
    (place.people?.flatMap((person) => person.emails ?? []) ?? []).map((email) => ({ ...email, source: 'person' as const })),
  );
  const allPlaceEmails: EmailData[] = selected.flatMap((place) =>
    (place.company_emails ?? []).map((email) => ({ ...email, source: 'place_data' as const })),
  );

  const verifiablePersonEmails = collectVerifiableEmails('person', allPersonEmails);
  const verifiablePlaceEmails = collectVerifiableEmails('place_data', allPlaceEmails);
  const totalVerifiable = verifiablePersonEmails.length + verifiablePlaceEmails.length;

  const handleCloseDmModal = () => {
    setIsDMModalOpen(false);
    setModalLockedPersonIds([]);
  };

  const handleOpenDmModal = () => {
    setModalLockedPersonIds(lockedPersonIds);
    setIsDMModalOpen(true);
  };

  const handleCloseVerifyModal = () => {
    setIsVerifyModalOpen(false);
    setModalPersonEmails([]);
    setModalPlaceEmails([]);
  };

  const handleOpenVerifyModal = () => {
    setModalPersonEmails(verifiablePersonEmails);
    setModalPlaceEmails(verifiablePlaceEmails);
    setIsVerifyModalOpen(true);
  };

  return (
    <>
      <div
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-[slideUp_0.22s_cubic-bezier(0.22,1,0.36,1)_both]
          bg-[#1C1B22] text-white rounded-2xl px-4 py-3
          flex flex-wrap items-center gap-3
          shadow-[0_8px_32px_rgba(0,0,0,0.22)]
          max-w-[calc(100vw-2rem)] mx-4 sm:mx-0"
      >
        <span className="text-[13px] font-semibold text-white/70 whitespace-nowrap">
          <b className="text-white">{selectedIds.size}</b> selected
        </span>

        <div className="w-px h-4 bg-white/15 hidden sm:block" />

        {lockedPersonIds.length > 0 && (
          <button
            onClick={handleOpenDmModal}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-[#8A3033] text-white text-[12px] font-bold whitespace-nowrap
              shadow-[0_2px_8px_rgba(138,48,51,0.35)] hover:bg-[#722629] transition-colors"
          >
            <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="6" r="3" stroke="white" strokeWidth="1.5" fill="none" />
              <path d="M2 14c0-3.3 2.7-5 6-5s6 1.7 6 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none" />
            </svg>
            Find {lockedPersonIds.length} owner email{lockedPersonIds.length !== 1 ? 's' : ''}
          </button>
        )}

        {totalVerifiable > 0 && (
          <button
            onClick={handleOpenVerifyModal}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg border border-white/25 text-white text-[12px] font-bold whitespace-nowrap
              hover:bg-white/10 transition-colors"
          >
            Verify {totalVerifiable} email{totalVerifiable !== 1 ? 's' : ''}
          </button>
        )}

        <button
          onClick={onClear}
          className="text-white/40 hover:text-white/80 transition-colors text-base leading-none ml-1 flex-shrink-0"
          aria-label="Clear selection"
        >
          ×
        </button>
      </div>

      {isDMModalOpen && modalLockedPersonIds.length > 0 && (
        <BulkDMUnlockModal
          isOpen={isDMModalOpen}
          onClose={handleCloseDmModal}
          personIds={modalLockedPersonIds}
          searchId={searchId}
        />
      )}

      {isVerifyModalOpen && (
        <EmailVerificationModal
          isOpen
          onClose={handleCloseVerifyModal}
          personEmails={modalPersonEmails}
          placeEmails={modalPlaceEmails}
          emailType="both"
          searchId={searchId}
          bulkMode={false}
        />
      )}
    </>
  );
};

export default FloatingSelectionBar;
